// Constants and static data for Pockedex

// Rarities considered "hits" for pack opening analytics — EX/ex-class
// pulls, full arts, and secrets. Matching is substring-based (see
// isHitRarity), and the API's naming flipped between eras: Scarlet &
// Violet says "Ultra Rare"/"Hyper Rare", Sword & Shield says
// "Rare Ultra"/"Rare Secret"/"Rare Rainbow", so both spellings are here.
export const HIT_RARITIES = [
  // Scarlet & Violet era
  'Double Rare',              // ex
  'Ultra Rare',               // full-art ex/supporter (also matches "Shiny Ultra Rare")
  'Illustration Rare',
  'Special Illustration Rare',
  'Hyper Rare',               // gold secret
  // Sword & Shield era and earlier
  'Rare Ultra',
  'Rare Secret',
  'Rare Rainbow',
  'Rare Holo V',              // also matches VMAX/VSTAR via substring
  'Rare Holo GX',
  'Rare Holo EX',
  'Amazing Rare',
  'Radiant Rare',
  // Legacy naming kept for back-compat
  'Secret Rare',
  'Rainbow Rare',
  'Gold Rare'
];

// A card counts as a hit if its rarity contains any HIT_RARITIES entry.
export const isHitRarity = (rarity) =>
  Boolean(rarity) && HIT_RARITIES.some((r) => rarity.includes(r));

// Initial collection data (your existing collection from prototype)
export const INITIAL_COLLECTION = {
  'sv3pt5-1': { 'Regular': 2, 'Reverse Holo': 1 },
  'sv3pt5-3': { 'Regular': 1 },
  'sv3pt5-6': { 'Regular': 1 }, 
  'sv3pt5-25': { 'Regular': 4, 'Reverse Holo': 2 },
  'sv3pt5-183': { 'Regular': 1 },
  'sv3pt5-184': { 'Regular': 1 }
};

// Pack opening statistics (from your prototype)
export const INITIAL_PACK_STATS = {
  totalPacks: 520,
  seriesStats: {
    "Scarlet & Violet": { packs: 95, hits: 27 },
    "Sword & Shield": { packs: 180, hits: 45 },
    "Sun & Moon": { packs: 125, hits: 32 },
    "XY": { packs: 85, hits: 18 },
    "Platinum": { packs: 15, hits: 3 },
    "POP": { packs: 8, hits: 1 },
    "Other": { packs: 7, hits: 2 },
    "Neo": { packs: 3, hits: 1 },
    "NP": { packs: 2, hits: 0 },
    "HeartGold & SoulSilver": { packs: 0, hits: 0 },
    "Gym": { packs: 0, hits: 0 },
    "EX": { packs: 0, hits: 0 }
  },
  setStats: {
    "sv3pt5": { packs: 45, hits: 15, lastOpened: "2025-01-15" },
    "sv4": { packs: 38, hits: 11, lastOpened: "2025-01-10" },
    "sv5": { packs: 42, hits: 13, lastOpened: "2025-01-12" }
  }
};

// Rarity pill buckets for FilterBar — collapses the long tail of exact
// rarity strings (Rare Holo, Ultra Rare, Secret Rare, ...) into 4 groups.
export const RARITY_BUCKETS = [
  { key: 'common', label: 'Common' },
  { key: 'uncommon', label: 'Uncommon' },
  { key: 'rare', label: 'Rare' },
  { key: 'rare-plus', label: 'Rare+' },
];

export const matchesRarityBucket = (cardRarity, bucketKey) => {
  if (!bucketKey || bucketKey === 'all') return true;
  if (bucketKey === 'common') return cardRarity === 'Common';
  if (bucketKey === 'uncommon') return cardRarity === 'Uncommon';
  if (bucketKey === 'rare') return cardRarity === 'Rare';
  if (bucketKey === 'rare-plus') {
    return Boolean(cardRarity) && !['Common', 'Uncommon', 'Rare'].includes(cardRarity);
  }
  // Fallback: treat unrecognized keys as an exact rarity match (back-compat)
  return cardRarity === bucketKey;
};

// Standard Pokemon TCG energy types with display colors
export const ENERGY_TYPES = [
  { name: 'Grass',     color: '#4CAF50' },
  { name: 'Fire',      color: '#F44336' },
  { name: 'Water',     color: '#2196F3' },
  { name: 'Lightning', color: '#FFC107' },
  { name: 'Psychic',   color: '#9C27B0' },
  { name: 'Fighting',  color: '#795548' },
  { name: 'Darkness',  color: '#37474F' },
  { name: 'Metal',     color: '#90A4AE' },
  { name: 'Dragon',    color: '#FF9800' },
  { name: 'Fairy',     color: '#E91E63' },
  { name: 'Colorless', color: '#BDBDBD' },
];

// Default app settings
export const DEFAULT_SETTINGS = {
  completionView: 'base',
  theme: 'light',
  autoSave: true,
  notifications: true,
  packStatsEnabled: true,
  // Copies to keep of each card variant — anything above this quantity is
  // automatically offered for trade (overridable per card)
  tradeThreshold: 1
};

// API configuration
export const API_CONFIG = {
  localServer: 'http://localhost:3001',
  timeout: 10000, // 10 seconds
  retryAttempts: 3
};

// UI constants
export const COMPLETION_VIEWS = {
  BASE: 'base',
  MASTER: 'master'
};

export const FILTER_OPTIONS = {
  RARITY: 'all',
  TYPE: 'all',
  OWNERSHIP: 'all'
};

// Mock data for development (if server is offline)
export const MOCK_SET_151 = {
  id: 'sv3pt5',
  name: '151',
  series: 'Scarlet & Violet',
  total: 207,
  printedTotal: 165,
  releaseDate: '2023/06/16',
  images: {
    symbol: 'https://images.pokemontcg.io/sv3pt5/symbol.png',
    logo: 'https://images.pokemontcg.io/sv3pt5/logo.png'
  }
};

// Centering analysis scenarios (for simulation)
export const CENTERING_SCENARIOS = [
  { result: true, message: '✅ Grade Worthy! Excellent centering detected.' },
  { result: true, message: '✅ Grade Worthy! Good centering for grading.' },
  { result: false, message: '❌ Keep for collection. Left border is too thick.' },
  { result: false, message: '❌ Keep for collection. Top/bottom centering is off.' },
  { result: false, message: '❌ Keep for collection. Rotation detected.' }
];

// Error messages
export const ERROR_MESSAGES = {
  SERVER_OFFLINE: 'Pokemon server is offline. Please start your local server.',
  API_ERROR: 'Error connecting to Pokemon API. Please try again.',
  LOAD_CARDS_ERROR: 'Failed to load cards for this set.',
  SAVE_ERROR: 'Failed to save your collection data.',
  IMPORT_ERROR: 'Failed to import collection data.'
};